import { defineStore } from "pinia";
import { computed, ref } from "vue";

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastOptions {
  action?: ToastAction;
  ariaLabelCloseBtn?: string;
  forceBarStyle?: boolean;
}

export interface ToastItem extends ToastOptions {
  id: string;
  text: string;
}

function normalizeToastText(message: string | Error): string {
  if (message instanceof Error) {
    return message.message || "Something went wrong";
  }

  return message;
}

function createToastId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const useToastStore = defineStore("toast", () => {
  const toasts = ref<ToastItem[]>([]);

  const activeToast = computed(() => toasts.value[0] ?? null);

  function showToast(message: string | Error, options: ToastOptions = {}): string {
    const toast: ToastItem = {
      id: createToastId(),
      text: normalizeToastText(message),
      action: options.action,
      ariaLabelCloseBtn: options.ariaLabelCloseBtn ?? "Dismiss",
      forceBarStyle: options.forceBarStyle,
    };

    toasts.value.push(toast);
    return toast.id;
  }

  function removeToast(id: string): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id);
  }

  function clearToasts(): void {
    toasts.value = [];
  }

  return {
    toasts,
    activeToast,
    showToast,
    removeToast,
    clearToasts,
  };
});
